import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Checkbox,
  FormControlLabel,
  Link,
  Typography,
  Alert,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CreditCardInfo } from '../../types/creditCardInfo';
import { APIHOST } from '../../common';
import { useApp } from '../AppContext';
import creditCardType from "credit-card-type";
import { set } from 'date-fns';

interface SubmitError {
  message: string;
  code: string | number;
}

interface CreditCardEntryProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
}

const initialFormState: CreditCardInfo = {
  cardType: '',
  niceType: '',
  cardNumber: '',
  cardHolderName: '',
  expirationMonth: '',
  expirationYear: '',
  cvv: '',
  creditCardZipCode: '',
};

export default function CreditCardEntry({ open, onClose, orderId }: CreditCardEntryProps) {
  const [formData, setFormData] = useState<CreditCardInfo>(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<CreditCardInfo>>({});
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);
  const [order, setOrder] = useState<any>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { setSnackbarInput } = useApp();

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId || !open) return;
      
      try {
        setLoadingOrder(true);
        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await fetch(`${APIHOST}/api/orders/${orderId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch order: ${response.status} - ${response.statusText}`);
        }

        const orderData = await response.json();
        setOrder(orderData.data);
      } catch (error) {
        console.error('Error fetching order:', error);
        setSnackbarInput({
          message: error instanceof Error ? error.message : 'Failed to fetch order information',
          severity: 'error'
        });
      } finally {
        setLoadingOrder(false);
      }
    };

    fetchOrder();
  }, [orderId, open, setSnackbarInput]);

  const handleInputChange = (field: keyof CreditCardInfo) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    
    // Format card number with spaces
    if (field === 'cardNumber') {
      const formattedValue = value.replace(/[^0-9]/g, '').replace(/(.{4})/g, '$1 ').trim();
      const typeMatches = creditCardType(formattedValue.replace(/\s/g, ''));
      const cardType = typeMatches.length === 1 ? typeMatches[0].type : '';
      const niceType = typeMatches.length === 1 ? typeMatches[0].niceType : '';
     
      setFormData(prev => ({ ...prev, [field]: formattedValue, cardType, niceType }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSelectChange = (field: keyof CreditCardInfo) => (event: any) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<CreditCardInfo> = {};
    
    if (!formData.cardNumber || formData.cardNumber.replace(/\s/g, '').length < 13) {
      newErrors.cardNumber = 'Please enter a valid card number';
    }
    
    if (!formData.cardHolderName.trim()) {
      newErrors.cardHolderName = 'Cardholder name is required';
    }
    
    if (!formData.expirationMonth) {
      newErrors.expirationMonth = 'Expiration month is required';
    }
    
    if (!formData.expirationYear) {
      newErrors.expirationYear = 'Expiration year is required';
    }
    
    if (!formData.cvv || formData.cvv.length < 3) {
      newErrors.cvv = 'Please enter a valid CVV';
    }
    
    if (!formData.creditCardZipCode.trim() || formData.creditCardZipCode.trim().length < 5) {
      newErrors.creditCardZipCode = 'Zip / Postal code must be at least 5 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && termsAccepted;
  };

  const handleSubmit = async () => {
    // Keep the alert visible until the user explicitly re-submits.
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Clean card number (remove spaces)
      const cleanedData = {
        ...formData,
        cardNumber: formData.cardNumber.replace(/\s/g, ''),
      };

      const response = await fetch(`${APIHOST}/api/orders/${orderId}/submitCreditCard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(cleanedData),
      });

      if (!response.ok) {
        const responseText = await response.text();
        let errorMessage = `Failed to submit credit card information: ${response.statusText || 'Unknown error'}`;
        let errorCode: string | number = response.status;

        if (responseText) {
          try {
            const parsedResponse = JSON.parse(responseText);
            const rawMessage = parsedResponse?.error?.message ?? parsedResponse?.message;
            const rawCode = parsedResponse?.error?.code;

            if (rawCode) {
              errorCode = rawCode;
            }

            if (typeof rawMessage === 'string' && rawMessage.trim()) {
              try {
                const parsedMessage = JSON.parse(rawMessage);
                errorMessage = parsedMessage?.moreInfo || parsedMessage?.message || rawMessage;
                if (parsedMessage?.code) {
                  errorCode = parsedMessage.code;
                }
              } catch {
                errorMessage = rawMessage;
              }
            }
          } catch {
            errorMessage = responseText;
          }
        }

        setSubmitError({ message: errorMessage, code: errorCode });
        setSnackbarInput({
          message: `${errorMessage} || error code: ${errorCode}`,
          severity: 'error'
        });
        return;
      }

      const result = await response.json();
      
      // Success! Clear form and show success message
      setFormData(initialFormState);
      setSubmitError(null);
      setSnackbarInput({
        message: 'Credit card information submitted successfully',
        severity: 'success'
      });
      onClose();
      
    } catch (error) {
      console.error('Error submitting credit card:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit credit card information';
      setSubmitError({ message: errorMessage, code: 'CLIENT_ERROR' });
      setSnackbarInput({
        message: `${errorMessage} || error code: CLIENT_ERROR`,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData(initialFormState);
      setErrors({});
      setTermsAccepted(false);
      onClose();
    }
  };

  // Check if form has any errors or missing required fields
  const hasFormErrors = (): boolean => {
    // Check for existing validation errors
    const hasValidationErrors = Object.values(errors).some(error => error && error.trim() !== '');
    
    // Check for missing required fields
    const hasEmptyRequiredFields = (
      !['mastercard','visa','american-express','discover'].includes(formData.cardType) ||
      !formData.cardNumber.trim() ||
      !formData.cardHolderName.trim() ||
      !formData.expirationMonth ||
      !formData.expirationYear ||
      !formData.cvv.trim() ||
      !formData.creditCardZipCode.trim() ||
      !termsAccepted
    );
    
    return hasValidationErrors || hasEmptyRequiredFields;
  };

  // Generate months
  const months = Array.from({ length: 12 }, (_, i) => {
    const month = (i + 1).toString().padStart(2, '0');
    return { value: month, label: month };
  });

  // Generate years (current year + 20 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => {
    const year = (currentYear + i).toString();
    return { value: year, label: year };
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Add Credit Card Information
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            <Grid size={{xs: 12}}>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Accepted Cards: Visa, MasterCard, American Express, Discover 
              </Typography>
            </Grid>
            <Grid size={{xs: 12}}>
              {formData.cardType && ['mastercard','visa','american-express','discover'].includes(formData.cardType)  && (
                <Box sx={{height: '24px'}}>{formData.niceType}</Box>
              )}
              {formData.cardType &&!['mastercard','visa','american-express','discover'].includes(formData.cardType) && (
                <Box sx={{height: '24px', mb: 2}}>
                  <Alert severity="info" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>
                    Unsupported Card Type: {formData.niceType}
                  </Alert>
                </Box>
              )}
              {!formData.cardType && (
                <Box sx={{height: '24px'}}>
                  Please enter your card number below``
                </Box>
              )}
            </Grid>
            <Grid size={{xs: 12}}>
              <TextField
                fullWidth
                label="Card Number"
                value={formData.cardNumber}
                onChange={handleInputChange('cardNumber')}
                error={!!errors.cardNumber}
                helperText={errors.cardNumber}
                placeholder="1234 5678 9012 3456"
                type="tel" 
                autoComplete="cc-number" 
                inputProps={{ maxLength: 23 }} // 16 digits + 3 spaces
              />
            </Grid>
            
            <Grid size={{xs: 12}}>
              <TextField
                fullWidth
                label="Cardholder Name"
                value={formData.cardHolderName}
                onChange={handleInputChange('cardHolderName')}
                error={!!errors.cardHolderName}
                helperText={errors.cardHolderName}
                placeholder="John Doe"
                type="text"
                autoComplete="cc-name"
              />
            </Grid>
            
            <Grid size={{xs: 6}}>
              <FormControl fullWidth error={!!errors.expirationMonth}>
                <InputLabel>Expiration Month</InputLabel>
                <Select
                  value={formData.expirationMonth}
                  onChange={handleSelectChange('expirationMonth')}
                  label="Expiration Month"
                  autoComplete="cc-exp-month"
                >
                  {months.map(month => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{xs: 6}}>
              <FormControl fullWidth error={!!errors.expirationYear}>
                <InputLabel>Expiration Year</InputLabel>
                <Select
                  value={formData.expirationYear}
                  onChange={handleSelectChange('expirationYear')}
                  label="Expiration Year"
                  autoComplete="cc-exp-year"
                >
                  {years.map(year => (
                    <MenuItem key={year.value} value={year.value}>
                      {year.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{xs: 12, sm: 6}}>
              <TextField
                fullWidth
                label="CVV"
                value={formData.cvv}
                onChange={handleInputChange('cvv')}
                error={!!errors.cvv}
                helperText={errors.cvv || 'Security code on back of card'}
                placeholder="123"
                inputProps={{ maxLength: 4 }}
                type="tel"
                autoComplete="cc-csc"
              />
            </Grid>
            
            <Grid size={{xs: 12, sm: 6}}>
              <TextField
                fullWidth
                label="Zip Code"
                value={formData.creditCardZipCode}
                onChange={handleInputChange('creditCardZipCode')}
                error={!!errors.creditCardZipCode}
                helperText={errors.creditCardZipCode}
                placeholder="12345"
                type="text"
              />
            </Grid>
          </Grid>
          
          {/* Order Total */}
          <Box sx={{ 
            mt: 3, 
            p: 2, 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                Total Amount:
              </Box>
              <Box sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: 'primary.main' }}>
                ${order?.invoicedTotal?.toFixed(2)}
              </Box>
            </Box>
          </Box>
          
          {/* Terms and Conditions Checkbox */}
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  color="primary"
                />
              }
              label={<span>I agree to the <Link href='/paymentCardTermsAndCondtions' target='_blank'>Terms and Conditions</Link> for payment processing</span>}
            />
          </Box>
         
        </Box>
        <Box>
          {submitError && (
            <Alert severity="error" sx={{ width: '100%', mb: 1 }}>
              {`Error ${submitError.code}: ${submitError.message}`}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={submitting || hasFormErrors()}
        >
          {submitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Submitting...
            </>
          ) : (
            'Submit Credit Card'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}